import * as React from 'react';
import {useSelector} from 'react-redux';
import {Link as RouterLink} from 'react-router-dom';

import classNames from 'classnames';

import {formEncodedApi} from 'nutshell-core/api';
import {getSingularEntityType, mapEntityToDisplayOption} from 'nutshell-core/utils';
import type {DisplayOptionEntity} from 'nutshell-core/utils';

import {CardWidgetEmptyState} from 'shells/empty-states/card-widget-empty-state';
// @ts-expect-error
import {CloseIcon} from 'shells/icon/svg/close-icon';
import {FileTextRegularIcon} from 'shells/icon/svg/file-text-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {PeepTwoLine} from 'shells/peep/peep-two-line';
import {SectionLabel} from 'shells/typography/section-label';
import {useDebounce} from 'shells/utils/hooks/use-debounce';

import {ACCOUNTS_FORM_KEY} from '../../entity-form/forms/account-form-connected';
import {
    CONTACTS_FORM_KEY,
    ENTITY_FORM_TYPE as CONTACTS_ENTITY_FORM_TYPE,
} from '../../entity-form/forms/contact-form-connected';

import type {EntityFormType} from '../types';

import {peepFormSelector, getPeepUrl, shouldShowDuplicateEntities} from './helpers';

import styles from './entity-form-sidebar.module.css';

type Props = {
    entityType: EntityFormType | null | undefined;
};

export const EntityFormSidebar = (props: Props) => {
    const formKey =
        props.entityType === CONTACTS_ENTITY_FORM_TYPE ? CONTACTS_FORM_KEY : ACCOUNTS_FORM_KEY;
    const rawFormValues = useSelector((state) => peepFormSelector(state, formKey));

    const [peepResults, setPeepResults] = React.useState<DisplayOptionEntity[]>([]);
    const [isDismissed, setIsDismissed] = React.useState<boolean>(false);
    const [hasEverShownResults, setHasEverShownResults] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const stringFormValues = JSON.stringify(rawFormValues);
    const debouncedFormValues = useDebounce(stringFormValues, 700);

    React.useEffect(() => {
        const parsedFields = JSON.parse(debouncedFormValues);
        const shouldSearch = shouldShowDuplicateEntities(parsedFields);

        if (!shouldSearch || isDismissed) {
            // If sidebar has been opened before, keep it open (will show empty state)
            // Otherwise do nothing
            setIsLoading(false);
            setPeepResults([]);

            return;
        }

        setIsLoading(true);

        const findDuplicateEntities = () => {
            formEncodedApi
                .get('/solr/find-potential-duplicate-entities.json', {
                    rows: 20,
                    entityType:
                        props.entityType === CONTACTS_ENTITY_FORM_TYPE ? 'contact' : 'account',
                    fields: parsedFields,
                })
                // @ts-expect-error
                .then((response) => response.json())
                .then((data) => {
                    const newResults = data.results.map(mapEntityToDisplayOption);
                    setPeepResults(newResults);
                    setIsLoading(false);

                    // Track that we've shown results at least once
                    if (newResults.length > 0) {
                        setHasEverShownResults(true);
                    }
                });
        };

        findDuplicateEntities();
    }, [props.entityType, debouncedFormValues, isDismissed]);

    if (!props.entityType || isDismissed || (!hasEverShownResults && peepResults.length === 0)) {
        return null;
    }

    const shouldShowDuplicateCount = peepResults.length > 0;

    return (
        <>
            <div className={styles['sidebar-modal-overlay']} />
            <div className={styles['sidebar-container']}>
                <div className={styles.sidebar}>
                    <div className={styles['sidebar-header']}>
                        <Stack spacing={8} isFullWidth={true}>
                            <Stack
                                isHorizontal={true}
                                justifyContent='space-between'
                                alignItems='center'
                                isFullWidth={true}
                            >
                                <SectionLabel
                                    noBottomBorder={true}
                                    noBottomMargin={true}
                                    color='dark'
                                >
                                    Potential duplicates{' '}
                                    {shouldShowDuplicateCount ? `(${peepResults.length})` : ''}
                                </SectionLabel>
                                <CloseIcon
                                    size={12}
                                    cursor='pointer'
                                    onClick={() => setIsDismissed(true)}
                                />
                            </Stack>
                        </Stack>
                    </div>
                    {peepResults.length > 0 ? (
                        <div
                            className={classNames(styles['results-container'], {
                                [styles['results-loading']]: isLoading,
                            })}
                        >
                            {peepResults.map((peep) => (
                                <div key={peep.id} className={styles['peep-result']}>
                                    <Link
                                        // @ts-expect-error
                                        as={RouterLink}
                                        key={peep.id}
                                        to={getPeepUrl(peep.id, peep.type)}
                                        preventUnderline={true}
                                        newTab={true}
                                    >
                                        <PeepTwoLine
                                            key={peep.id}
                                            name={
                                                peep.value ||
                                                `Unnamed ${getSingularEntityType(peep.type)}`
                                            }
                                            avatarUrl={peep.avatarUrl}
                                            initials={peep.initials}
                                            type={peep.type}
                                            relatedEntityName={peep.secondaryInfo}
                                        />
                                        {(peep.primaryEmailText || peep.primaryPhoneText) && (
                                            <div className={styles['tertiary-info']}>
                                                {peep.primaryEmailText || peep.primaryPhoneText}
                                            </div>
                                        )}
                                    </Link>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={styles['empty-state-container']}>
                            <CardWidgetEmptyState
                                text={`No duplicate ${
                                    props.entityType === 'contacts' ? 'people' : 'companies'
                                } found`}
                                icon={FileTextRegularIcon}
                                iconSize={40}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
